import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';

/**
 * Public documentation page for Meta data deletion (A4.4).
 *
 * Reviewed by Meta as part of App Review — the URL is filed in the App
 * Dashboard "Data Deletion Request URL" field. Mounted outside the
 * authenticated app shell so reviewers (and users) can reach it without
 * a Flouri account.
 *
 * Two distinct Meta-side flows fire two distinct callbacks at Flouri:
 * - Removing Flouri from Facebook settings → POST /auth/meta/deauthorize
 *   → soft deactivation, data retained for re-link recovery.
 * - "Request Data Deletion" in Facebook settings → POST /auth/meta/data-deletion
 *   → hard cascade across meta_ads_data.* + meta_ads_credentials.
 * This page documents both for humans; the backend handlers handle the
 * Meta-side contracts.
 */
export function MetaDataDeletion() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" aria-label="Flouri home">
            <Logo />
          </Link>
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-semibold text-gray-900 mb-4">
          Meta Data Deletion
        </h1>
        <p className="text-gray-700 mb-8">
          This page describes how Flouri handles deletion of data we receive
          from Meta (Facebook and Instagram) on your behalf.
        </p>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            What data Flouri stores from Meta
          </h2>
          <p className="text-gray-700 mb-3">
            When you connect a Meta ad account to Flouri, we store:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Your Meta ad account ID, business name, currency, and timezone</li>
            <li>A long-lived access token, used to fetch your campaign data</li>
            <li>Your Meta user ID (used to honor deletion requests)</li>
            <li>
              Campaign, ad set, ad, and creative data — what we pull daily from the
              Meta Marketing API at the account level
            </li>
            <li>Daily performance metrics (impressions, clicks, spend, conversions)</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            How to delete your data
          </h2>
          <p className="text-gray-700 mb-3">
            There are three paths, with different consequences:
          </p>

          <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
            1. Remove Flouri from your Facebook settings
          </h3>
          <p className="text-gray-700 mb-2">
            Go to{' '}
            <a
              href="https://www.facebook.com/settings?tab=business_tools"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Facebook Business Integrations
            </a>{' '}
            and remove Flouri. Meta will automatically notify us, and we will
            deactivate your Meta credentials within minutes. <strong>Your
            campaign data is retained</strong> so you can reconnect later
            without losing history.
          </p>

          <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
            2. Disconnect from inside Flouri
          </h3>
          <p className="text-gray-700 mb-2">
            On your Flouri Account page, click "Disconnect" next to the Meta
            account you want to unlink. Same effect as path (1) — credentials
            deactivated, data retained.
          </p>

          <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
            3. Request full deletion (right to erasure)
          </h3>
          <p className="text-gray-700 mb-2">
            For complete removal of every Meta-sourced row Flouri holds about
            you — including historical campaign performance — email{' '}
            <a
              href="mailto:support@flouri.ai?subject=Meta%20data%20deletion%20request"
              className="text-blue-600 hover:text-blue-800"
            >
              support@flouri.ai
            </a>{' '}
            with subject "Meta data deletion request" from the email address
            associated with your account. We will process the deletion
            manually and email you back to confirm completion.
          </p>
          <p className="text-gray-700 mb-2">
            Path (1) above (removing Flouri at Facebook → choosing "Request
            Data Deletion") triggers Meta's automated deletion handshake. In
            that case we issue a confirmation code on Meta's side and you can
            poll deletion status at{' '}
            <code className="text-sm bg-gray-100 px-1 rounded">
              /legal/meta-deletion-status?code=&lt;your-code&gt;
            </code>{' '}
            for at least 30 days. The email path (path 3 above) is processed
            by hand and does not currently issue a pollable confirmation code.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Status of a deletion request
          </h2>
          <p className="text-gray-700">
            If you received a confirmation code via Meta's deletion flow, you
            can check the status at{' '}
            <Link
              to="/legal/meta-deletion-status"
              className="text-blue-600 hover:text-blue-800"
            >
              /legal/meta-deletion-status
            </Link>
            . The status is reachable for at least 30 days from the request.
            Codes are issued only via the Meta-initiated path (path 1 above);
            email-initiated requests are processed manually with email
            confirmation instead.
          </p>
        </section>

        <footer className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-600">
          <p>
            For questions, email{' '}
            <a
              href="mailto:support@flouri.ai"
              className="text-blue-600 hover:text-blue-800"
            >
              support@flouri.ai
            </a>
            .
          </p>
        </footer>
      </main>
    </div>
  );
}
